import {
  Field,
  FieldDescription,
  FieldLabel,
  FieldLegend,
  FieldSet,
  RadioGroup,
  RadioGroupItem,
} from '@monksflow/monks-ui';
import { format } from 'date-fns';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DATE_BOUND_TODAY,
  isWithinDateBounds,
  parseCalendarDay,
  resolveDateBounds,
  toCalendarDay,
} from '@/entities/brief';
import { resolveDateFnsLocale } from '@/shared/i18n/date-fns-locale';
import { DatePicker } from '@/shared/ui';

export interface DateConfigFieldsProps {
  config: Record<string, unknown>;
  onChange: (patch: Record<string, unknown>) => void;
  disabled: boolean;
}

// Wire values persisted in `config.format` — must match the backend's
// `DateSectionConfigDto.format` enum exactly. Not user-facing copy; the
// translated label comes from `labelKey` below.

const FORMATS = [
  {
    // eslint-disable-next-line i18next/no-literal-string -- protocol value: DateSectionConfigDto's `@IsIn(['Start date only', 'Start + End date'])` validates this exact string server-side; translating it would break the wire contract.
    value: 'Start date only',
    labelKey: 'knowledgeHub.briefSectionConfigFields.formatStartDateOnly',
  },
  {
    // eslint-disable-next-line i18next/no-literal-string -- see the entry above.
    value: 'Start + End date',
    labelKey: 'knowledgeHub.briefSectionConfigFields.formatStartAndEndDate',
  },
] as const;

/* eslint-disable i18next/no-literal-string -- radio values, never rendered */
type BoundMode = 'none' | 'today' | 'specific';
const MIN_MODES = [
  { mode: 'none', labelKey: 'knowledgeHub.briefSectionConfigFields.noMinimum' },
  { mode: 'today', labelKey: 'knowledgeHub.briefSectionConfigFields.today' },
  { mode: 'specific', labelKey: 'knowledgeHub.briefSectionConfigFields.specificDate' },
] as const;
const MAX_MODES = [
  { mode: 'none', labelKey: 'knowledgeHub.briefSectionConfigFields.noMaximum' },
  { mode: 'specific', labelKey: 'knowledgeHub.briefSectionConfigFields.specificDate' },
] as const;
/* eslint-enable i18next/no-literal-string */

interface DateBoundChoiceProps {
  legend: string;
  modes: typeof MIN_MODES | typeof MAX_MODES;
  value: unknown;
  onWrite: (next: string | null) => void;
  isDayDisabled: (day: Date) => boolean;
  describedBy: string;
  disabled: boolean;
}

/**
 * "Specific date" with no day picked yet stores no bound, so the choice is held locally until a
 * day is picked; otherwise the radio would fall back to "No minimum" the moment it is clicked.
 * It starts set for a loaded day, because re-clicking the picked day clears it.
 */
function DateBoundChoice(props: DateBoundChoiceProps) {
  const { t, i18n } = useTranslation('knowledge');
  const legendId = useId();
  const pickerId = useId();
  const [isPickingDay, setIsPickingDay] = useState(() => parseCalendarDay(props.value) !== null);
  const fixedDay = parseCalendarDay(props.value);
  const mode: BoundMode =
    props.value === DATE_BOUND_TODAY ? 'today' : fixedDay || isPickingDay ? 'specific' : 'none';

  const handleModeChange = (next: string) => {
    setIsPickingDay(next === 'specific');
    if (next === 'today') props.onWrite(DATE_BOUND_TODAY);
    else if (next === 'none' || !fixedDay) props.onWrite(null);
  };

  return (
    <FieldSet>
      <FieldLegend id={legendId} variant="label">
        {props.legend}
      </FieldLegend>
      <RadioGroup
        value={mode}
        onValueChange={handleModeChange}
        disabled={props.disabled}
        aria-labelledby={legendId}
        aria-describedby={props.describedBy}
      >
        {props.modes.map((option) => (
          <label key={option.mode} className="flex items-center gap-2 text-sm">
            <RadioGroupItem value={option.mode} />
            {t(option.labelKey)}
          </label>
        ))}
      </RadioGroup>
      {mode === 'specific' && (
        <div>
          <label htmlFor={pickerId} className="sr-only">
            {fixedDay
              ? t('knowledgeHub.briefSectionConfigFields.boundWithDate', {
                  bound: props.legend,
                  date: format(fixedDay, 'PPP', { locale: resolveDateFnsLocale(i18n.language) }),
                })
              : props.legend}
          </label>
          <DatePicker
            id={pickerId}
            value={fixedDay}
            disabled={props.disabled}
            onChange={(next) => props.onWrite(next ? toCalendarDay(next) : null)}
          >
            <DatePicker.Trigger />
            <DatePicker.Content disabledDates={props.isDayDisabled} />
          </DatePicker>
        </div>
      )}
    </FieldSet>
  );
}

/**
 * Each picker greys out the days that would put the bounds out of order, and the server refuses
 * an inverted fixed pair, so the two bounds need no order check here.
 */
export function DateConfigFields(props: DateConfigFieldsProps) {
  const { t } = useTranslation('knowledge');
  const hintId = useId();
  const format = typeof props.config.format === 'string' ? props.config.format : FORMATS[0].value;
  const bounds = resolveDateBounds(props.config);
  const fixedMax = parseCalendarDay(props.config.maxDate);

  return (
    <div className="flex flex-col gap-md">
      <Field>
        <FieldLabel>{t('knowledgeHub.briefSectionConfigFields.format')}</FieldLabel>
        <RadioGroup
          value={format}
          onValueChange={(next: string) => props.onChange({ format: next })}
          disabled={props.disabled}
        >
          {FORMATS.map((option) => (
            <label key={option.value} className="flex items-center gap-2 text-sm">
              <RadioGroupItem value={option.value} />
              {t(option.labelKey)}
            </label>
          ))}
        </RadioGroup>
      </Field>

      <div className="grid grid-cols-2 gap-sm">
        <DateBoundChoice
          legend={t('knowledgeHub.briefSectionConfigFields.minimumDate')}
          modes={MIN_MODES}
          value={props.config.minDate}
          onWrite={(next) => props.onChange({ minDate: next })}
          isDayDisabled={(day) => !isWithinDateBounds(day, { min: null, max: fixedMax })}
          describedBy={hintId}
          disabled={props.disabled}
        />
        <DateBoundChoice
          legend={t('knowledgeHub.briefSectionConfigFields.maximumDate')}
          modes={MAX_MODES}
          value={props.config.maxDate}
          onWrite={(next) => props.onChange({ maxDate: next })}
          isDayDisabled={(day) => !isWithinDateBounds(day, { min: bounds.min, max: null })}
          describedBy={hintId}
          disabled={props.disabled}
        />
      </div>
      <FieldDescription id={hintId}>
        {t('knowledgeHub.briefSectionConfigFields.dateRangeHint')}
      </FieldDescription>
    </div>
  );
}
